import type { ILineItem } from '@/types/IOrderV2';

/**
 * Base product names that require design work
 * These products may appear as exact matches (single variant) or with variant suffixes (multi-variant)
 */
const DESIGN_REQUIRED_BASE_PRODUCTS = [
  'Tapt Custom',
  'Sample -  Custom Digital Business Card',
  'Tapt Black',
  'Tapt Custom Stand',
  'Replacement Card',
  'Custom Google Review Stand',
];

/**
 * Checks if a product name requires design work
 * Handles both exact matches and variant patterns (e.g., "Tapt Stand Custom - Black")
 * @param productName - The product name from a line item
 * @returns true if the product requires design work, false otherwise
 */
export const isDesignWorkProductName = (productName: string): boolean => {
  // Check for exact match first (single variant products)
  if (DESIGN_REQUIRED_BASE_PRODUCTS.includes(productName)) {
    return true;
  }

  // Check for variant pattern (multi-variant products: "Product Name - Variant")
  return DESIGN_REQUIRED_BASE_PRODUCTS.some(baseProduct =>
    productName.startsWith(`${baseProduct} - `),
  );
};

/**
 * Checks if an order contains products that require design work
 * @param productsOrdered - Array of line items from an order, can be null
 * @returns true if any line item requires design work, false otherwise
 */
export const hasDesignWorkProducts = (
  productsOrdered: ILineItem[] | null,
): boolean => {
  if (!productsOrdered || productsOrdered.length === 0) {
    return false;
  }

  return productsOrdered.some(lineItem =>
    isDesignWorkProductName(lineItem.name),
  );
};

/**
 * Gets the list of products in an order that require design work
 * @param productsOrdered - Array of line items from an order, can be null
 * @returns Array of line items that require design work
 */
export const getDesignWorkProducts = (
  productsOrdered: ILineItem[] | null,
): ILineItem[] => {
  if (!productsOrdered || productsOrdered.length === 0) {
    return [];
  }

  return productsOrdered.filter(lineItem =>
    isDesignWorkProductName(lineItem.name),
  );
};
